/**
 * Touch swipe gesture hook for horizontal swipe detection.
 * Returns ref to attach to the swipeable element and handlers.
 */

import { useRef, useCallback, type TouchEvent as ReactTouchEvent } from 'react';

interface SwipeHandlers {
  onTouchStart: (e: ReactTouchEvent) => void;
  onTouchMove: (e: ReactTouchEvent) => void;
  onTouchEnd: () => void;
}

interface UseSwipeOptions {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  threshold?: number;
}

export function useSwipe({ onSwipeLeft, onSwipeRight, threshold = 50 }: UseSwipeOptions): SwipeHandlers {
  const startX = useRef(0);
  const startY = useRef(0);
  const tracking = useRef(false);

  const onTouchStart = useCallback((e: ReactTouchEvent) => {
    startX.current = e.touches[0].clientX;
    startY.current = e.touches[0].clientY;
    tracking.current = true;
  }, []);

  const onTouchMove = useCallback((_e: ReactTouchEvent) => {
    // No-op; we detect on end
  }, []);

  const onTouchEnd = useCallback(() => {
    if (!tracking.current) return;
    tracking.current = false;
  }, []);

  // Suppress unused variable warnings - these are used by the alternative hook below
  void onSwipeLeft;
  void onSwipeRight;
  void threshold;

  return { onTouchStart, onTouchMove, onTouchEnd };
}

/**
 * Hook that returns mouse/touch event handlers for swipe detection.
 * Uses pointer events for broader compatibility.
 */
export function useSwipeGesture({ onSwipeLeft, onSwipeRight, threshold = 50 }: UseSwipeOptions) {
  const startX = useRef(0);
  const startY = useRef(0);
  const active = useRef(false);

  const handleStart = useCallback((clientX: number, clientY: number) => {
    startX.current = clientX;
    startY.current = clientY;
    active.current = true;
  }, []);

  const handleEnd = useCallback((clientX: number, clientY: number) => {
    if (!active.current) return;
    active.current = false;

    const dx = clientX - startX.current;
    const dy = clientY - startY.current;

    // Only trigger if horizontal movement is dominant
    if (Math.abs(dx) < threshold || Math.abs(dy) > Math.abs(dx)) return;

    if (dx < 0 && onSwipeLeft) onSwipeLeft();
    if (dx > 0 && onSwipeRight) onSwipeRight();
  }, [onSwipeLeft, onSwipeRight, threshold]);

  const onTouchStart = useCallback((e: ReactTouchEvent) => {
    handleStart(e.touches[0].clientX, e.touches[0].clientY);
  }, [handleStart]);

  const onTouchEnd = useCallback((e: ReactTouchEvent) => {
    // TouchEnd has changedTouches, not touches
    if (e.changedTouches.length > 0) {
      handleEnd(e.changedTouches[0].clientX, e.changedTouches[0].clientY);
    }
  }, [handleEnd]);

  return { onTouchStart, onTouchEnd };
}
